// ============================================================================
// App.jsx — Router principale dell'applicazione
// ============================================================================

import { useState, useEffect, useCallback, useRef } from "react";
import { useOreData, useCantieri, usePromemoria } from "./hooks/useOreData";
import { supabase, isSupabaseConfigured } from "./lib/supabase";
import RoleSelection from "./components/auth/RoleSelection";
import EmployeeLogin from "./components/auth/EmployeeLogin";
import AdminLogin from "./components/auth/AdminLogin";
import EmployeeApp from "./components/employee/EmployeeApp";
import AdminDashboard from "./components/admin/AdminDashboard";

const DIRECT_DIP_ID = (() => {
  const match = window.location.pathname.match(/^\/d\/(\d+)$/);
  if (match) {
    const id = match[1];
    document.cookie = `directDipId=${id}; path=/; max-age=31536000; SameSite=Lax`;
    try { localStorage.setItem("directDipId", id); } catch {}
    return Number(id);
  }
  const path = window.location.pathname;
  if (path.startsWith("/admin") || path.startsWith("/dipendente")) return null;
  // Sulla root "/", ripristina il link diretto solo se siamo in PWA standalone
  // (operaio che apre l'app dalla home screen). In un browser normale la root
  // deve sempre mostrare la scelta ruolo, altrimenti un cookie rimasto da una
  // visita precedente a /d/:id "intrappola" l'utente sulla schermata dipendente.
  if (path === "/") {
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches
      || window.navigator.standalone === true;
    if (!isStandalone) return null;
  }
  const cookieMatch = document.cookie.match(/directDipId=(\d+)/);
  if (cookieMatch) return Number(cookieMatch[1]);
  const saved = localStorage.getItem("directDipId");
  if (saved) return Number(saved);
  return null;
})();
const IS_INVALID_DIRECT = window.location.pathname.startsWith("/d/") && !window.location.pathname.match(/^\/d\/(\d+)$/);

// Rileva il flag di recupero password nell'URL (può essere nell'hash #type=recovery
// oppure nella query ?type=recovery). Se lo trova, entriamo subito in modalità
// recovery a prescindere dal path di atterraggio, e forziamo la schermata admLogin
// così l'utente atterra direttamente sul form "imposta nuova password".
const INITIAL_RECOVERY = (() => {
  try {
    const hash = window.location.hash || "";
    const search = window.location.search || "";
    return /type=recovery/.test(hash) || /type=recovery/.test(search)
      || /[?&#]access_token=/.test(hash) || /[?&#]access_token=/.test(search);
  } catch { return false; }
})();

function getInitialScreen() {
  const path = window.location.pathname;
  if (IS_INVALID_DIRECT) return "invalidLink";
  if (INITIAL_RECOVERY) return "admLogin";
  if (DIRECT_DIP_ID) return "empApp";
  if (path.startsWith("/dipendente")) return "empLogin";
  if (path.startsWith("/admin")) return "admLogin";
  return "role";
}

export default function App() {
  const [screen, setScreen] = useState(getInitialScreen);
  const [empUser, setEmpUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(!isSupabaseConfigured);
  const [recoveryMode, setRecoveryMode] = useState(INITIAL_RECOVERY);
  // Ref sempre aggiornato con il valore corrente di recoveryMode, serve ai
  // listener async di Supabase Auth (che catturano lo stato al momento del
  // subscribe e non vedrebbero gli aggiornamenti successivi).
  const recoveryRef = useRef(INITIAL_RECOVERY);
  useEffect(() => { recoveryRef.current = recoveryMode; }, [recoveryMode]);

  // Verifica sessione admin all'avvio e ascolta cambi di stato
  useEffect(() => {
    if (!isSupabaseConfigured) return;

    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      const hasSession = !!data?.session;
      // IMPORTANTE: se l'utente è arrivato qui da un link di recupero password,
      // anche se Supabase ha già creato una sessione NON dobbiamo mandarlo in
      // dashboard — deve prima impostare la nuova password. Altrimenti basta
      // un link di reset per avere accesso admin senza mai cambiare la password.
      if (hasSession && !INITIAL_RECOVERY) {
        setScreen((prev) => {
          // Se ho già una sessione e sono in admLogin, salto diretto alla dashboard.
          // Se sono in un'altra schermata (ruolo, dipendente, ecc.) non forzo: potrebbe
          // essere un operaio che sta usando lo stesso browser.
          if (prev === "admLogin") return "admDash";
          return prev;
        });
      }
      setAuthChecked(true);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        // L'utente è arrivato dal link di reset password via email.
        // Forziamo la schermata admLogin in modalità "imposta nuova password".
        setRecoveryMode(true);
        setScreen("admLogin");
      } else if (event === "SIGNED_IN" && recoveryRef.current) {
        // In alcuni casi Supabase emette SIGNED_IN invece di PASSWORD_RECOVERY
        // per i token di recupero. Se sappiamo di essere partiti da un URL di
        // recupero, restiamo bloccati sul form "nuova password" finché non ha
        // davvero cambiato la password.
        setScreen("admLogin");
      } else if (event === "SIGNED_OUT") {
        setRecoveryMode(false);
        setScreen((prev) => (prev === "admDash" ? "admLogin" : prev));
      }
    });
    return () => {
      cancelled = true;
      sub?.subscription?.unsubscribe();
    };
  }, []);

  const { oreData, loading, addOre, setOreData, upsertOreEntry, deleteOreEntry, refreshOreData, realtimeStatus } = useOreData();
  const { cantieri, addCantiere, deleteCantiere, restoreCantiere, archivedCantieri } = useCantieri();
  const { promemoria, addPromemoria, deletePromemoria } = usePromemoria();
  const [dipendenti, setDipendenti] = useState(() => {
    try { return JSON.parse(localStorage.getItem("baseDipendenti") || "[]") || []; }
    catch { return []; }
  });

  // Salva lista dipendenti su Supabase
  const saveDipendenti = useCallback((list) => {
    setDipendenti(list);
    localStorage.setItem("baseDipendenti", JSON.stringify(list));
    if (isSupabaseConfigured) {
      supabase.from("app_settings")
        .upsert([{ key: "baseDipendenti", value: list }], { onConflict: "key" })
        .then(({ error }) => { if (error) console.error("Errore salvataggio dipendenti:", error); });
    }
  }, []);

  // Carica dipendenti da Supabase + realtime sync
  useEffect(() => {
    if (!isSupabaseConfigured) return;

    supabase.from("app_settings").select("key, value").eq("key", "baseDipendenti").single()
      .then(({ data, error }) => {
        if (error || !data) return;
        const list = Array.isArray(data.value) ? data.value : [];
        setDipendenti(list);
        localStorage.setItem("baseDipendenti", JSON.stringify(list));
        if (DIRECT_DIP_ID) {
          setEmpUser((prev) => prev || list.find((d) => d.id === DIRECT_DIP_ID) || null);
        }
      });

    const channel = supabase
      .channel("dip_sync_realtime")
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: "app_settings",
        filter: "key=eq.baseDipendenti",
      }, (payload) => {
        const list = payload.new?.value;
        if (list && Array.isArray(list)) {
          setDipendenti(list);
          localStorage.setItem("baseDipendenti", JSON.stringify(list));
          if (DIRECT_DIP_ID) {
            setEmpUser((prev) => prev || list.find((d) => d.id === DIRECT_DIP_ID) || null);
          }
        }
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  // Aggiorna URL quando cambia schermata (ma non sovrascrivere /d/:id)
  useEffect(() => {
    const path = window.location.pathname;
    if (path.match(/^\/d\/\d+$/)) return;
    // Se siamo in modalità recupero password NON tocchiamo l'URL: l'hash
    // contiene ancora il token che supabase-js deve leggere.
    if (recoveryMode) return;
    // Se c'è un access_token nell'hash, aspettiamo che supabase-js lo consumi
    // prima di riscrivere l'URL, altrimenti il token va perso.
    if (window.location.hash && /access_token=/.test(window.location.hash)) return;
    if (screen === "empLogin" || screen === "empApp") {
      window.history.pushState({}, "", "/dipendente");
    } else if (screen === "admLogin" || screen === "admDash") {
      window.history.pushState({}, "", "/admin");
    } else {
      window.history.pushState({}, "", "/");
    }
  }, [screen]);

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "var(--bg)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <img src="/favicon.svg" alt="Mattone Rosso" style={{ width: 56, height: 56, borderRadius: 14, marginBottom: 16 }} />
          <p style={{ color: "var(--text-muted)", fontSize: 14 }}>Caricamento...</p>
        </div>
      </div>
    );
  }

  if (DIRECT_DIP_ID && screen !== "empApp") {
    if (empUser) {
      return (
        <EmployeeApp
          user={empUser}
          onLogout={() => window.location.reload()}
          oreData={oreData}
          addOre={addOre}
          cantieri={cantieri}
          refreshOreData={refreshOreData}
          promemoria={promemoria}
          hideLogout
        />
      );
    }
    return (
      <div style={{ minHeight: "100vh", background: "var(--bg)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <img src="/favicon.svg" alt="Mattone Rosso" style={{ width: 56, height: 56, borderRadius: 14, marginBottom: 16 }} />
          <p style={{ color: "var(--text-muted)", fontSize: 14 }}>Caricamento...</p>
        </div>
      </div>
    );
  }

  switch (screen) {
    case "invalidLink":
      return (
        <div style={{ minHeight: "100vh", background: "var(--bg)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ textAlign: "center", maxWidth: 400 }}>
            <img src="/favicon.svg" alt="Mattone Rosso" style={{ width: 56, height: 56, borderRadius: 14, marginBottom: 16 }} />
            <h2 style={{ color: "var(--text)", fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Link non valido</h2>
            <p style={{ color: "var(--text-muted)", fontSize: 15, marginBottom: 24 }}>Questo link non funziona. Chiedi al tuo datore di lavoro di inviarti il link corretto.</p>
          </div>
        </div>
      );

    case "role":
      return (
        <RoleSelection
          onSelect={(role) => setScreen(role === "employee" ? "empLogin" : "admLogin")}
        />
      );

    case "empLogin":
      return (
        <EmployeeLogin
          onBack={() => setScreen("role")}
          onLogin={(user) => { setEmpUser(user); setScreen("empApp"); }}
          dipendenti={dipendenti}
        />
      );

    case "empApp":
      if (empUser) {
        return (
          <EmployeeApp
            user={empUser}
            onLogout={DIRECT_DIP_ID ? () => window.location.reload() : () => { setEmpUser(null); setScreen("empLogin"); }}
            oreData={oreData}
            addOre={addOre}
            cantieri={cantieri}
            refreshOreData={refreshOreData}
            promemoria={promemoria}
            hideLogout={!!DIRECT_DIP_ID}
          />
        );
      }
      if (DIRECT_DIP_ID) {
        return (
          <div style={{ minHeight: "100vh", background: "var(--bg)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ textAlign: "center" }}>
              <img src="/favicon.svg" alt="Mattone Rosso" style={{ width: 56, height: 56, borderRadius: 14, marginBottom: 16 }} />
              <p style={{ color: "var(--text-muted)", fontSize: 14 }}>Caricamento...</p>
            </div>
          </div>
        );
      }
      return (
        <EmployeeLogin
          onBack={() => setScreen("role")}
          onLogin={(user) => { setEmpUser(user); setScreen("empApp"); }}
          dipendenti={dipendenti}
        />
      );

    case "admLogin":
      return (
        <AdminLogin
          onBack={() => setScreen("role")}
          onLogin={() => setScreen("admDash")}
          recoveryMode={recoveryMode}
          onRecoveryComplete={() => {
            setRecoveryMode(false);
            window.history.replaceState({}, "", "/admin");
          }}
        />
      );

    case "admDash":
      return (
        <AdminDashboard
          onLogout={async () => {
            if (isSupabaseConfigured) {
              try { await supabase.auth.signOut(); } catch {}
            }
            setScreen("admLogin");
          }}
          oreData={oreData}
          setOreData={setOreData}
          upsertOreEntry={upsertOreEntry}
          deleteOreEntry={deleteOreEntry}
          cantieri={cantieri}
          addCantiere={addCantiere}
          deleteCantiere={deleteCantiere}
          restoreCantiere={restoreCantiere}
          archivedCantieri={archivedCantieri}
          promemoria={promemoria}
          addPromemoria={addPromemoria}
          deletePromemoria={deletePromemoria}
          dipendenti={dipendenti}
          saveDipendenti={saveDipendenti}
        />
      );

    default:
      return (
        <RoleSelection
          onSelect={(role) => setScreen(role === "employee" ? "empLogin" : "admLogin")}
        />
      );
  }
}
